import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DATE_LOCALE, formatDate } from '../i18n/date-format';

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const TIME_FORMAT: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

/** @param locale The `Intl` locale for the time of day (`DATE_LOCALE`). */
export function formatRelativeTime(
  date: Date,
  translate: TranslateService,
  locale: string
): string {
  const now = new Date();

  if (isSameDay(date, now)) {
    return translate.instant('at') + ' ' + formatDate(date, TIME_FORMAT, locale);
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) {
    return translate.instant('yesterday');
  }

  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 7) {
    return translate.instant('[count] days ago', { count: diffDays });
  }
  if (diffDays < 14) {
    return translate.instant('last week');
  }
  if (diffDays < 30) {
    return translate.instant('[count] weeks ago', { count: Math.floor(diffDays / 7) });
  }
  if (diffDays < 60) {
    return translate.instant('last month');
  }
  if (diffDays < 365) {
    return translate.instant('[count] months ago', { count: Math.floor(diffDays / 30) });
  }
  if (diffDays < 730) {
    return translate.instant('last year');
  }
  return translate.instant('[count] years ago', { count: Math.floor(diffDays / 365) });
}

@Pipe({ name: 'relativeTime', standalone: true })
export class RelativeTimePipe implements PipeTransform {
  private readonly _translate = inject(TranslateService);
  private readonly _locale = inject(DATE_LOCALE);

  /**
   * @param _language The active language (`LanguageService.active()`). Unused
   *   here, but it makes this pure pipe re-run after a language switch.
   */
  transform(date: Date, _language?: string): string {
    return formatRelativeTime(date, this._translate, this._locale());
  }
}
